import { Array, Data, Effect, Match, Option, Schema } from 'effect';

import {
  PaymentMethodCodeSchema,
  ShippingMethodCodeSchema,
  storedPlacementId,
  OrderShipmentIdSchema,
  OrderPaymentIdSchema,
} from '@vs-shop/engine/service/orderShipment/OrderShipment';
import type { OrderShipmentStorageT } from '@vs-shop/engine/service/orderShipment/OrderShipment';
import { PlacementIdSchema } from '@vs-shop/engine/service/shipping/Placement';

export const OrderSubmissionSchema = Schema.Struct({
  shipmentId: OrderShipmentIdSchema,
  paymentId: OrderPaymentIdSchema,
  shippingMethod: ShippingMethodCodeSchema,
  paymentMethod: PaymentMethodCodeSchema,
  placementId: Schema.Option(PlacementIdSchema),
});

export class IncompleteOrderSubmissionError extends Data.TaggedError(
  'IncompleteOrderSubmissionError'
)<{
  message: string;
  cause?: unknown;
}> {}

type OrderWithShipmentAndPayment = {
  readonly shipments: ReadonlyArray<{ readonly id?: number | null }>;
  readonly payments: ReadonlyArray<{ readonly id?: number | null }>;
};

/** Validates every field required by the final shipment and payment requests. */
export const orderSubmission = (
  order: Option.Option<OrderWithShipmentAndPayment>,
  stored: Option.Option<OrderShipmentStorageT>
) => {
  const values = Option.all({
    shipmentId: Option.flatMap(order, (value) =>
      Option.flatMap(Array.head(value.shipments), (shipment) => Option.fromNullishOr(shipment.id))
    ),
    paymentId: Option.flatMap(order, (value) =>
      Option.flatMap(Array.head(value.payments), (payment) => Option.fromNullishOr(payment.id))
    ),
    stored,
  });

  return Match.value(values).pipe(
    Match.when(Option.isNone, () =>
      Effect.fail(
        new IncompleteOrderSubmissionError({
          message: 'The Order has incomplete shipment or payment data',
        })
      )
    ),
    Match.when(Option.isSome, ({ value }) =>
      Schema.decodeEffect(OrderSubmissionSchema)({
        shipmentId: value.shipmentId,
        paymentId: value.paymentId,
        shippingMethod: value.stored.shipment.shippingMethodCode,
        paymentMethod: value.stored.payment.paymentCode,
        placementId: Option.fromNullOr(storedPlacementId(value.stored)),
      }).pipe(
        Effect.mapError(
          (cause) =>
            new IncompleteOrderSubmissionError({
              message: 'The Order has incomplete shipment or payment data',
              cause,
            })
        )
      )
    ),
    Match.exhaustive
  );
};
